/**
 * Password Reset request page - replaces account/password_reset.html template
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { AuthLayout, BRAND_COLORS } from "../components/AuthLayout";
import { renderFieldErrors } from "../components/FieldErrorList";
import {
  useAuthContext,
  useFieldErrors,
  useNonFieldErrors,
} from "../hooks/useAuthContext";
import { gettext } from "@services/i18n";

export function PasswordResetPage() {
  const { csrfToken, formData, isAuthenticated } = useAuthContext();
  const nonFieldErrors = useNonFieldErrors();
  const emailErrors = useFieldErrors("email");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState(formData?.email ?? "");

  const handleSubmit = () => {
    setIsSubmitting(true);
    // Let the form submit naturally to Django
  };

  return (
    <AuthLayout
      title={gettext("Password Reset")}
      subtitle={gettext(
        "Forgot your password? Enter your e-mail address below, and we'll send you an e-mail allowing you to reset it.",
      )}
    >
      {/* Form */}
      <Box
        component="form"
        method="POST"
        action="/accounts/password/reset/"
        onSubmit={handleSubmit}
        sx={{
          padding: "0 2.5rem 2.5rem",
          backgroundColor: "white",
        }}
      >
        <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken} />

        {/* Already logged in warning */}
        {isAuthenticated && (
          <Alert severity="info" sx={{ marginBottom: "1rem" }}>
            {gettext("Note: you are already logged in.")}
          </Alert>
        )}

        {/* Non-field errors */}
        {nonFieldErrors.length > 0 && (
          <Alert
            severity="error"
            sx={{ marginBottom: "1rem" }}
            data-testid="password-reset-error"
          >
            {nonFieldErrors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}

        {/* Email Field */}
        <TextField
          fullWidth
          name="email"
          type="email"
          label={gettext("Email")}
          placeholder={gettext("Enter your email address")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={emailErrors.length > 0}
          helperText={renderFieldErrors(emailErrors)}
          required
          sx={{
            marginBottom: "1.5rem",
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontFamily:
                "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              "&:hover fieldset": {
                borderColor: BRAND_COLORS.inputBorder,
              },
              "&.Mui-focused fieldset": {
                borderColor: BRAND_COLORS.inputFocus,
              },
            },
            "& .MuiInputLabel-root": {
              fontWeight: 500,
              color: BRAND_COLORS.text.primary,
            },
          }}
          inputProps={{
            "data-testid": "password-reset-email-input",
          }}
        />

        {/* Submit Button */}
        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{
            padding: "0.875rem",
            backgroundColor: BRAND_COLORS.primary,
            borderRadius: 2,
            fontSize: "0.9375rem",
            fontWeight: 600,
            textTransform: "none",
            fontFamily: "'Montserrat', sans-serif",
            marginTop: "0.5rem",
            "&:hover": {
              backgroundColor: BRAND_COLORS.primaryHover,
              boxShadow: 2,
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          }}
          data-testid="password-reset-submit-button"
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            gettext("Send Password Reset")
          )}
        </Button>

        {/* Back to Sign In Link */}
        <Link
          href="/accounts/login/"
          sx={{
            display: "inline-block",
            marginTop: "1rem",
            color: BRAND_COLORS.link,
            fontSize: "0.875rem",
            textDecoration: "none",
            fontWeight: 500,
            "&:hover": {
              color: BRAND_COLORS.linkHover,
              textDecoration: "underline",
            },
          }}
          data-testid="back-to-login-link"
        >
          {gettext("Back to Sign In")}
        </Link>
      </Box>
    </AuthLayout>
  );
}
